/**
 * One truth for what a customer-typeahead dropdown may show (Issue #822).
 *
 * The office audit reproduced two traps on production: the previous query's
 * cached rows rendering under freshly-typed text, and "No customers found.
 * Create new?" appearing while the search was still running — the offered
 * action being the one that creates a duplicate customer. Both come from
 * rendering off `results.length` alone. Every customer typeahead (jobs form,
 * quick-book panel, commercial form) derives its dropdown state from this
 * function instead, so a pending search can never render as a negative
 * result and stale rows can never pose as answers.
 */
export type CustomerSearchView =
    /** No search text — show the recent-customers list, if any. */
    | "recents"
    /** One character typed — too short to search; ask for more. */
    | "keep-typing"
    /** Typed text hasn't settled, or the fetch is in flight. */
    | "searching"
    /** The settled query has results. */
    | "results"
    /** The settled query fetched and found nothing. */
    | "empty";

/** True when the server should be queried for this settled text at all —
 *  a single character substring-matches nearly every record through site
 *  addresses and contact names, which reads as an unfiltered list. */
export function isSearchableCustomerQuery(debouncedText: string): boolean {
    return debouncedText.trim().length !== 1;
}

export function customerSearchView(opts: {
    /** What's in the input box right now. */
    inputText: string;
    /** The debounced text the current query key was built from. */
    debouncedText: string;
    /** React Query's isFetching for the customer-search query. */
    isFetching: boolean;
    resultCount: number;
}): CustomerSearchView {
    const typed = opts.inputText.trim();
    const settled = opts.debouncedText.trim();
    if (typed.length === 1) return "keep-typing";
    // Stale key (debounce window) or in-flight fetch: whatever data we hold
    // belongs to a different question. Never results, never "none found".
    if (typed !== settled || opts.isFetching) return "searching";
    if (settled.length === 0) return "recents";
    return opts.resultCount > 0 ? "results" : "empty";
}
